/*
 * Quick-add sentences: the rule behind Important pet info's chips (SF 34,
 * 34d, 67) and Review and book's note for the groomer. Tapping one adds its
 * sentence to the end of the note with the part in [brackets] selected, so
 * typing replaces it. An added one shows ✓ and isn't added twice; the text
 * stays the client's to edit or delete.
 *
 * Nothing records which were used: ✓ is read from the note itself, as a
 * sentence with the template's fixed words and anything in the brackets, so
 * it survives a reload and the brackets being filled in.
 */

export interface QuickAddTemplate<K extends string = string> {
  key: K;
  label: string;
  sentence: string;
}

export interface AddResult {
  text: string;
  /** The first [bracket] in the added sentence, to select; null when it has none. */
  select: [number, number] | null;
  /** True when it wasn't added because the note would pass its limit. */
  tooLong?: boolean;
}

export interface QuickAdd<K extends string> {
  /** True when the note already holds this template's sentence (filled in or not). */
  isAdded: (note: string, template: QuickAddTemplate<K>) => boolean;
  /**
   * The note with the template's sentence at the end (after a space, or on
   * its own), and the part to select. `fill` replaces the first bracket.
   */
  addTemplate: (note: string, template: QuickAddTemplate<K>, fill?: string) => AddResult;
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
// ’ and ' are the same letter to a person typing.
const quotes = (s: string) => s.replace(/['’]/g, "['’]");

/** The quick-add rule for one list of sentences and one note's limit. */
export const quickAdd = <K extends string>(templates: QuickAddTemplate<K>[], max: number): QuickAdd<K> => {
  const patterns = new Map<K, RegExp>(
    templates.map(t => {
      const body = t.sentence
        .split(/\[[^\]]*\]/)
        .map(part => quotes(escape(part)))
        .join('[^.!?\\n]+?');
      // A sentence of its own: at the start, or after the end of another.
      return [t.key, new RegExp(`(^|[.!?)]\\s*|\\n\\s*)${body}`, 'i')];
    })
  );

  const isAdded = (note: string, template: QuickAddTemplate<K>): boolean =>
    patterns.get(template.key)!.test(note || '');

  const addTemplate = (note: string, template: QuickAddTemplate<K>, fill?: string): AddResult => {
    const current = note || '';
    if (isAdded(current, template)) return { text: current, select: null };
    let sentence = template.sentence;
    if (fill) sentence = sentence.replace(/\[[^\]]*\]/, fill.trim());
    const sep = current === '' || /\s$/.test(current) ? '' : ' ';
    const text = `${current}${sep}${sentence}`;
    if (text.length > max) return { text: current, select: null, tooLong: true };
    const start = current.length + sep.length;
    const bracket = /\[[^\]]*\]/.exec(sentence);
    return { text, select: bracket ? [start + bracket.index, start + bracket.index + bracket[0].length] : null };
  };

  return { isAdded, addTemplate };
};
